import { Add } from '@carbon/react/icons';
import {
  Button,
  ComposedModal,
  InlineLoading,
  InlineNotification,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Search,
} from '@carbon/react';
import { CreateCrmAccount } from 'components/create-crm-account/create-crm-account';
import { useAtom, useSetAtom } from 'jotai';
import { debounce } from 'lodash-es';
import { createCrmAccountMutationAtom } from 'pages/crm-tracts-subtracts/atoms';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CRMAccountResponse,
  EntityLinkResponse,
  EntityResponse,
  SalesforceAccountResponse,
} from 'types/api-responses';
import { convertToSalesforceAccount } from '../utils';
import { AccountSearchTable } from './account-search-table';
import { accountsSearchAtomPair, entityLinkingMutationAtom } from './atoms';
import { EntityInfoTable } from './entity-info-table';
import { useAccountCreation, useEntityLinking } from './hooks';
import style from './link-crm-account-modal.module.scss';

const DEFAULT_PAGE_SIZE = 16;

interface LinkCrmAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  entityId?: number;
  entity?: EntityResponse;
  onLinkSuccess?: () => void;
  initialSearchText?: string;
  existingLinkedAccounts?: CRMAccountResponse[];
  accountTypeOptions?: Array<{ value: string; label: string }>;
  accountSourceOptions?: Array<{ value: string; label: string }>;
}

export const LinkCrmAccountModal = ({
  isOpen,
  onClose,
  entityId,
  entity,
  onLinkSuccess,
  initialSearchText = '',
  existingLinkedAccounts = [],
  accountTypeOptions = [],
  accountSourceOptions = [],
}: LinkCrmAccountModalProps) => {
  const [accountsSearch, setAccountsSearch] = useAtom(
    accountsSearchAtomPair.searchAtom
  );

  // Debounced search function for account search
  const debouncedAccountSearch = useMemo(
    () =>
      debounce((value: string, page: number = 1, pageSize: number = 16) => {
        if (value.trim().length > 0) {
          setAccountsSearch((current) => ({
            text: value,
            page,
            pageSize: pageSize || current?.pageSize || DEFAULT_PAGE_SIZE,
          }));
        } else {
          setAccountsSearch(undefined);
        }
      }, 400),
    [setAccountsSearch]
  );

  const handleSearchAccounts = useCallback(
    (searchValue: string, page?: number, pageSize?: number) => {
      debouncedAccountSearch(searchValue, page, pageSize);
    },
    [debouncedAccountSearch]
  );

  const searchResults = {
    data: accountsSearch.data
      ? {
          results: accountsSearch.data.results,
          totalCount: accountsSearch.data.totalCount,
          pageSize: accountsSearch.data.pageSize,
        }
      : undefined,
    loading: accountsSearch.loading || false,
    currentPayload: accountsSearch.currentPayload,
  };
  const [searchText, setSearchText] = useState('');
  const [selectedAccounts, setSelectedAccounts] = useState<
    SalesforceAccountResponse[]
  >([]);
  const [existingAccountIds, setExistingAccountIds] = useState<string[]>([]);
  const [linkingState, setLinkingState] = useState({
    isLinking: false,
    channel: null as string | null,
    error: null as string | null,
    complete: false,
  });
  const [showCreatePanel, setShowCreatePanel] = useState(false);
  const [createAccountState, setCreateAccountState] = useState({
    isCreating: false,
    channel: null as string | null,
    events: [] as Array<{ name: string; label: string }>,
    error: null as string | null,
  });
  const [expandedAccounts, setExpandedAccounts] = useState(false);
  const [createdAccount, setCreatedAccount] = useState<{
    id: string | null;
    name: string | null;
    showNotification: boolean;
    salesforceId?: string; // Store the Salesforce ID from Pusher
  }>({
    id: null,
    name: null,
    showNotification: false,
    salesforceId: undefined,
  });
  const [hoveredAccountId, setHoveredAccountId] = useState<string | null>(null);

  const entityLinkingMutation = useSetAtom(entityLinkingMutationAtom);

  // Helper function to reset all state
  const resetAllState = useCallback(() => {
    setSearchText('');
    setSelectedAccounts([]);
    setExistingAccountIds([]);
    setLinkingState({
      isLinking: false,
      channel: null,
      error: null,
      complete: false,
    });
    setShowCreatePanel(false);
    setExpandedAccounts(false);
    setCreateAccountState({
      isCreating: false,
      channel: null,
      events: [],
      error: null,
    });
    setCreatedAccount({
      id: null,
      name: null,
      showNotification: false,
      salesforceId: undefined,
    });
    setHoveredAccountId(null);
    setAccountsSearch(undefined);
  }, [setAccountsSearch]);

  useEffect(() => {
    if (!isOpen) {
      resetAllState();
    } else {
      // When modal opens, populate with existing linked accounts if provided
      if (existingLinkedAccounts.length > 0) {
        const convertedAccounts = existingLinkedAccounts.map(
          convertToSalesforceAccount
        );
        setSelectedAccounts(convertedAccounts);
        // Track existing account IDs
        const allAccountIds = convertedAccounts.map((acc) => acc.salesforceId);
        setExistingAccountIds(allAccountIds);
      } else {
        setExistingAccountIds([]);
      }

      // Populate search if initialSearchText is provided
      if (initialSearchText) {
        setSearchText(initialSearchText);
        handleSearchAccounts(
          initialSearchText,
          1,
          searchResults?.currentPayload?.pageSize || DEFAULT_PAGE_SIZE
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialSearchText, resetAllState, handleSearchAccounts]);

  // Subscribe to Pusher channel for POST (linking) completion
  useEntityLinking({
    linkingState,
    setLinkingState,
    onLinkSuccess,
    onClose,
  });

  const handleSave = useCallback(() => {
    if (!entityId) {
      return;
    }

    // Find newly added accounts (not in existingAccountIds)
    const newlyAddedAccounts = selectedAccounts.filter(
      (acc) => !existingAccountIds.includes(acc.salesforceId)
    );

    // Save button should be enabled if there's a newly added account to link
    if (newlyAddedAccounts.length === 0) {
      return;
    }

    setLinkingState({
      isLinking: true,
      channel: null,
      error: null,
      complete: false,
    });

    // Link the first newly added account
    const accountToLink = newlyAddedAccounts[0];
    entityLinkingMutation({
      url: `/entities/${entityId}/entity_links`,
      type: 'POST',
      data: {
        accountId: accountToLink.salesforceId,
      },
      onSuccess: (data: EntityLinkResponse) => {
        const channel = data.resources?.notifications?.channel;
        if (channel) {
          setLinkingState((prev) => ({
            ...prev,
            channel,
          }));
        } else {
          setLinkingState({
            isLinking: false,
            channel: null,
            error: 'Failed to get workflow channel. Please try again.',
            complete: false,
          });
        }
      },
      onError: (error: unknown) => {
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Failed to link CRM account. Please try again.';
        setLinkingState({
          isLinking: false,
          channel: null,
          error: errorMessage,
          complete: false,
        });
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedAccounts,
    existingAccountIds,
    entityId,
    entityLinkingMutation,
    onLinkSuccess,
    onClose,
  ]);

  const handleSearchChange = ({
    target: { value },
  }: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(value);

    // Clear selected accounts (except existing linked ones) when search is cleared
    if (value.trim().length === 0) {
      setSelectedAccounts((prev) =>
        prev.filter((acc) => existingAccountIds.includes(acc.salesforceId))
      );
    }

    handleSearchAccounts(
      value,
      1,
      searchResults?.currentPayload?.pageSize || DEFAULT_PAGE_SIZE
    );
  };

  const createCrmAccountMutationState = useSetAtom(
    createCrmAccountMutationAtom
  );

  // Subscribe to Pusher channel for account creation
  useAccountCreation({
    createAccountState,
    setCreateAccountState,
    setShowCreatePanel,
    setCreatedAccount,
    setSearchText,
    handleSearchAccounts,
    searchResults,
    DEFAULT_PAGE_SIZE,
  });

  const searchResultsData = useMemo(
    () => searchResults?.data?.results || [],
    [searchResults?.data?.results]
  );
  const searchTotalCount = searchResults?.data?.totalCount || 0;
  const searchPageSize = searchResults?.data?.pageSize || DEFAULT_PAGE_SIZE;
  const searchPage = searchResults?.currentPayload?.page || 1;
  const isLoading = searchResults?.loading || false;

  // Auto-select the created account when search results are loaded
  // Match by the newly created account ID from Pusher when results come back
  useEffect(() => {
    // Use salesforceId if available (from Pusher), otherwise fallback to id
    const accountIdToMatch = createdAccount.salesforceId || createdAccount.id;

    // Only proceed if we have an account ID to match, search results are available, and not loading
    if (!accountIdToMatch || isLoading || searchResultsData.length === 0) {
      return;
    }

    // Find account by matching the newly created account ID from Pusher
    const foundAccount = searchResultsData.find(
      (acc) => acc.salesforceId === accountIdToMatch
    );

    if (foundAccount) {
      // Check if account is already selected to avoid unnecessary updates
      const isAlreadySelected = selectedAccounts.some(
        (acc) => acc.salesforceId === foundAccount.salesforceId
      );

      if (!isAlreadySelected) {
        setSelectedAccounts((prev) => {
          // Keep existing linked accounts, but replace any selected from search results
          const existingLinked = prev.filter((acc) =>
            existingAccountIds.includes(acc.salesforceId)
          );
          // Add the newly created account
          return [...existingLinked, foundAccount];
        });
      }
    }
  }, [
    createdAccount.salesforceId,
    createdAccount.id,
    searchResultsData,
    isLoading,
    selectedAccounts,
    existingAccountIds,
  ]);

  const handleLeftPanelSelect = useCallback(
    (account: SalesforceAccountResponse, checked: boolean) => {
      if (checked) {
        // Only allow one account to be selected at a time
        // Replace any existing selection with the new one
        setSelectedAccounts((prev) => {
          // Keep existing linked accounts, but replace any selected from search results
          const existingLinked = prev.filter((acc) =>
            existingAccountIds.includes(acc.salesforceId)
          );
          // Add the newly selected account
          return [...existingLinked, account];
        });
      } else {
        // Remove from selectedAccounts (only if it's not an existing linked account)
        setSelectedAccounts((prev) =>
          prev.filter((acc) => acc.salesforceId !== account.salesforceId)
        );
      }
    },
    [existingAccountIds]
  );

  const isLeftPanelSelected = useCallback(
    (accountId: string) => {
      return selectedAccounts.some((acc) => acc.salesforceId === accountId);
    },
    [selectedAccounts]
  );

  const isAccountAlreadyLinked = useCallback(
    (accountId: string) => {
      return existingAccountIds.includes(accountId);
    },
    [existingAccountIds]
  );

  if (!isOpen) return null;

  return (
    <ComposedModal
      open={isOpen}
      onClose={linkingState.isLinking ? undefined : onClose}
      size="lg"
      className={style.modal}
    >
      <ModalHeader>
        <h4 className={style.modalHeader}>Link CRM Account</h4>
      </ModalHeader>
      <ModalBody className={style.modalBody}>
        {linkingState.isLinking && (
          <div className={style.loadingOverlay}>
            <div className={style.loadingContent}>
              <InlineLoading description="Linking CRM account..." />
            </div>
          </div>
        )}
        {/* Notifications */}
        {linkingState.error && (
          <div className={style.notificationSection}>
            <InlineNotification
              className={style.bannerNotification}
              kind="error"
              title={linkingState.error}
              hideCloseButton
              onClose={() => {
                setLinkingState((prev) => ({ ...prev, error: null }));
                return true;
              }}
            />
          </div>
        )}

        {/* Main Content Area - Split when Create Panel is open */}
        <div
          className={
            showCreatePanel ? style.splitContainer : style.modalContent
          }
        >
          {/* Left Panel: Entity Info + Search Section */}
          <div className={style.leftPanel}>
            {/* Entity Information Table */}
            {entity && (
              <EntityInfoTable
                entity={entity}
                existingLinkedAccounts={existingLinkedAccounts}
                expandedAccounts={expandedAccounts}
                onToggleExpanded={() => setExpandedAccounts(!expandedAccounts)}
              />
            )}

            {/* Select CRM Account Section */}
            <div className={style.searchHeader}>
              <div className={style.searchTitle}>
                <h3 className={style.heading}>Select CRM Account*</h3>
                <p className={style.subheading}>
                  Search for your desired Entity and select it to continue.
                </p>
              </div>
              <div className={style.searchInputContainer}>
                <Search
                  labelText="Search"
                  placeholder="Search CRM accounts"
                  size="lg"
                  value={searchText}
                  onChange={handleSearchChange}
                />
              </div>

              {createdAccount.showNotification && (
                <InlineNotification
                  className={style.bannerNotification}
                  kind="success"
                  title="Successfully created CRM Account"
                  hideCloseButton={false}
                  lowContrast
                  onClose={() => {
                    setCreatedAccount({
                      id: null,
                      name: null,
                      showNotification: false,
                      salesforceId: undefined,
                    });
                    return true;
                  }}
                />
              )}
              <div className={style.searchResultsAndActions}>
                {searchText.trim().length > 0 && !isLoading && (
                  <div className={style.searchResultsCount}>
                    {searchTotalCount} Matches
                  </div>
                )}
                <div className={style.searchActions}>
                  <Button
                    kind="primary"
                    size="sm"
                    renderIcon={Add}
                    iconDescription="Create CRM Account"
                    disabled={
                      showCreatePanel ||
                      linkingState.isLinking ||
                      selectedAccounts.some(
                        (acc) => !existingAccountIds.includes(acc.salesforceId)
                      )
                    }
                    onClick={() => {
                      setShowCreatePanel(true);
                      setCreateAccountState((prev) => ({
                        ...prev,
                        error: null,
                      }));
                    }}
                  >
                    Create CRM Account
                  </Button>
                </div>
              </div>
            </div>

            <div className={style.tableSection}>
              <AccountSearchTable
                searchText={searchText}
                searchData={searchResultsData}
                searchTotalCount={searchTotalCount}
                searchPageSize={searchPageSize}
                searchPage={searchPage}
                isLoading={isLoading}
                hoveredAccountId={hoveredAccountId}
                onAccountSelect={handleLeftPanelSelect}
                onHoverAccount={setHoveredAccountId}
                onPaginate={(page, pageSize) => {
                  handleSearchAccounts(searchText, page, pageSize);
                }}
                isAccountSelected={isLeftPanelSelected}
                isAccountAlreadyLinked={isAccountAlreadyLinked}
              />
            </div>
          </div>

          {/* Create CRM Account Panel */}
          {showCreatePanel && (
            <div className={style.linkPanelWrapper}>
              <CreateCrmAccount
                isOpen={showCreatePanel}
                initialAccountName={entity?.name || ''}
                accountTypeOptions={accountTypeOptions}
                accountSourceOptions={accountSourceOptions}
                onClose={() => {
                  setShowCreatePanel(false);
                  setCreateAccountState({
                    isCreating: false,
                    channel: null,
                    events: [],
                    error: null,
                  });
                  setCreatedAccount({
                    id: null,
                    name: null,
                    showNotification: false,
                    salesforceId: undefined,
                  });
                }}
                onCreate={(payload) => {
                  setCreateAccountState({
                    isCreating: true,
                    channel: null,
                    events: [],
                    error: null,
                  });
                  // Store the account name for later search
                  setCreatedAccount({
                    id: null,
                    name: payload.accountName,
                    showNotification: false,
                  });
                  createCrmAccountMutationState({
                    url: '/crm/accounts',
                    type: 'POST',
                    data: {
                      name: payload.accountName,
                      type: payload.type,
                      source: payload.accountSource,
                      phone: payload.phoneNumber,
                      address: payload.address,
                      city: payload.city,
                      state: payload.state,
                      zip_code: payload.zipCode,
                    },
                    onSuccess: (response) => {
                      const channel =
                        response.resources?.notifications?.channel;
                      const events =
                        response.resources?.notifications?.events || [];
                      if (channel) {
                        setCreateAccountState((prev) => ({
                          ...prev,
                          channel,
                          events,
                        }));
                      }
                    },
                    onError: (error: unknown) => {
                      const errorMessage =
                        error instanceof Error
                          ? error.message
                          : 'Failed to create CRM account. Please try again.';
                      setCreateAccountState({
                        isCreating: false,
                        channel: null,
                        events: [],
                        error: errorMessage,
                      });
                      setCreatedAccount({
                        id: null,
                        name: null,
                        showNotification: false,
                        salesforceId: undefined,
                      });
                    },
                  });
                }}
                createAccountError={createAccountState.error}
                isCreatingAccount={createAccountState.isCreating}
              />
            </div>
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button
          kind="secondary"
          onClick={onClose}
          disabled={linkingState.isLinking}
        >
          Cancel
        </Button>
        <Button
          kind="primary"
          onClick={handleSave}
          disabled={
            !selectedAccounts.some(
              (acc) => !existingAccountIds.includes(acc.salesforceId)
            ) || linkingState.isLinking
          }
        >
          Save
        </Button>
      </ModalFooter>
    </ComposedModal>
  );
};
